'use client';

import React, { useState, useEffect } from "react";
import { X, Phone, MessageCircle, Calendar, Clock, MapPin, IndianRupee, User, Users, FileText, Mail, Search, Pencil, Undo2, ArrowRightLeft, Trash2, AlertTriangle } from "lucide-react";
import { useJigyasa, formatINR, APPOINTMENT_STAGE, isLiveStage, needsNextDate } from "./state";
import { StageChip, PrimaryButton, SecondaryButton, formatDate, formatDateTime, buildNextDate, todayPlusISODate, phoneIsValid, normalizePhone } from "./parts";
import { CapacityFitPanel, FootprintBanner, PastEnquiryBanner, PricingContextPanel } from "./intelligence";

// Programmatic history.back() calls (consuming a sheet's parked entry when it
// closes via X/backdrop) must not be mistaken for the user pressing Back.
let swallowNextPop = 0;
// Count of open sheets on a phone — the Ashraya bubble hides while > 0.
let shySheets = 0;

// Backdrop + slide-up sheet
export const Sheet = ({ open, onClose, children, testId, title, wide = false }) => {
  useEffect(() => {
    if (!open) return undefined;
    // Escape closes only the TOPMOST open sheet (last backdrop in DOM order),
    // so stacked sheets peel off one at a time.
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      const backdrops = document.querySelectorAll('[data-testid$="-backdrop"]');
      const top = backdrops[backdrops.length - 1];
      if (top && top.getAttribute("data-testid") === `${testId}-backdrop`) onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose, testId]);
  // Phones: while any sheet is open the Ashraya bubble steps aside — it was
  // sitting on top of the box's Add button (owner-reported).
  useEffect(() => {
    if (!open) return undefined;
    if (typeof window === "undefined" || window.innerWidth >= 640) return undefined;
    shySheets += 1;
    document.body.dataset.ashrayaShy = "true";
    return () => {
      shySheets -= 1;
      if (shySheets <= 0) {
        shySheets = 0;
        delete document.body.dataset.ashrayaShy;
      }
    };
  }, [open]);
  // Phone back button closes the topmost sheet instead of leaving the site.
  // Each open sheet parks one history entry; closing by X/backdrop swallows
  // its own entry so history never drifts. PHONE-ONLY: on desktop the intro
  // tour auto-opens/closes editor sheets in quick succession and the
  // push/back pairs race each other until the tab walks off to about:blank.
  useEffect(() => {
    if (!open) return undefined;
    if (typeof window === "undefined" || window.innerWidth >= 640) return undefined;
    window.history.pushState({ jigyasaSheet: testId }, "");
    let closedByBack = false;
    const onPop = () => {
      if (swallowNextPop > 0) { swallowNextPop -= 1; return; }
      const backdrops = document.querySelectorAll('[data-testid$="-backdrop"]');
      const top = backdrops[backdrops.length - 1];
      if (top && top.getAttribute("data-testid") === `${testId}-backdrop`) {
        closedByBack = true;
        onClose?.();
      }
    };
    window.addEventListener("popstate", onPop);
    return () => {
      window.removeEventListener("popstate", onPop);
      if (!closedByBack) {
        swallowNextPop += 1;
        window.history.back();
      }
    };
  }, [open, onClose, testId]);
  if (!open) return null;
  return (
    <div
      data-testid={`${testId}-backdrop`}
      className="fixed inset-0 z-40 flex items-end justify-center bg-zinc-900/40 sm:items-center"
      onClick={onClose}
    >
      <div
        data-testid={testId}
        className={`w-full ${wide ? "sm:max-w-3xl" : "sm:max-w-md"} max-h-[calc(100dvh-9rem)] overflow-y-auto rounded-t-2xl bg-white shadow-xl sm:max-h-[92vh] sm:rounded-2xl`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-zinc-200 bg-white px-5 py-3">
          <h3 className="text-lg font-semibold text-zinc-900">{title}</h3>
          <button
            data-testid={`${testId}-close`}
            onClick={onClose}
            className="rounded-full p-2 text-zinc-500 hover:bg-zinc-100"
            aria-label="Close"
          >
            <X size={20} />
          </button>
        </div>
        <div className="px-5 py-4">{children}</div>
      </div>
    </div>
  );
};

// -------------------- Remarks timeline --------------------
// Pilot feedback 11 Jun 2026: operators speak to a lead many times before the
// next stage happens. Every remark is auto-stamped with time + stage; stage
// changes are auto-logged into the same timeline as system entries.
const RemarksTimeline = ({ enquiry }) => {
  const { addRemark, costume, stages } = useJigyasa();
  const [text, setText] = useState("");
  const c = costume.copy;
  const remarks = [...(enquiry.remarks || [])].sort((a, b) => new Date(b.at) - new Date(a.at));
  const stageLabel = (key) => stages.find((s) => s.key === key)?.label || key;

  const submit = () => {
    if (!text.trim()) return;
    addRemark(enquiry.id, text.trim());
    setText("");
  };

  return (
    <div data-testid="remarks-timeline">
      <div className="mb-1 text-xs font-medium uppercase tracking-wider text-zinc-500">{c.remarksTitle}</div>
      <div className="mb-2 flex gap-2">
        <input
          data-testid="remark-input"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && submit()}
          placeholder={c.remarkPlaceholder}
          className="min-w-0 flex-1 rounded-lg border border-zinc-300 px-3 py-2.5 text-sm"
        />
        <button
          data-testid="remark-save"
          onClick={submit}
          disabled={!text.trim()}
          className="shrink-0 rounded-lg bg-rose-600 px-4 py-2 text-sm font-medium text-white hover:bg-rose-700 disabled:opacity-50"
        >
          {c.addRemarkButton}
        </button>
      </div>
      {remarks.length === 0 ? (
        <p data-testid="remarks-empty" className="rounded-lg bg-zinc-50 p-3 text-xs text-zinc-500">{c.remarksEmpty}</p>
      ) : (
        <ul className="space-y-1.5">
          {remarks.map((r) => (
            <li
              key={r.id}
              data-testid={`remark-item-${r.id}`}
              className={`rounded-lg p-2.5 text-sm ${
                r.system
                  ? "bg-zinc-100 text-zinc-500"
                  : "border border-amber-100 bg-amber-50 text-zinc-800"
              }`}
            >
              <div className="text-[11px] text-zinc-500">
                {formatDateTime(r.at)} · {stageLabel(r.stage)}{r.by ? ` · ${r.by}` : ""}
              </div>
              <div className={r.system ? "text-xs italic" : "mt-0.5"}>{r.text}</div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

// -------------------- Next action date prompt --------------------
// The two-tap heartbeat: after every interaction the operator sets ONE date
// (Tomorrow / In 2 days / In a week / Pick a date). For a scheduled visit/demo
// stage we also capture a time, so reminders can say "4 PM". Each save is
// auto-stamped to the timeline so the full "call tomorrow ×4" loop is preserved.
export const NextActionPrompt = ({ open, onClose, enquiry, isAppointment, onSaved }) => {
  const { updateEnquiry, addRemark } = useJigyasa();
  const [dateStr, setDateStr] = useState(todayPlusISODate(1));
  const [timeStr, setTimeStr] = useState("10:00");
  const [note, setNote] = useState("");
  const [noteOpen, setNoteOpen] = useState(false);

  useEffect(() => {
    if (open) {
      setDateStr(todayPlusISODate(1));
      setTimeStr("10:00");
      setNote("");
      setNoteOpen(false);
    }
  }, [open]);

  if (!enquiry) return null;

  const quick = [
    { label: "Tomorrow", days: 1 },
    { label: "In 2 days", days: 2 },
    { label: "In a week", days: 7 },
  ];

  const save = () => {
    const iso = buildNextDate(dateStr, isAppointment ? timeStr : null);
    if (!iso) return;
    updateEnquiry(enquiry.id, { next_followup_date: iso });
    const when = isAppointment ? formatDateTime(iso) : formatDate(iso);
    addRemark(
      enquiry.id,
      isAppointment ? `Visit / demo set for ${when}` : `Next contact set for ${when}`,
      { system: true }
    );
    if (note.trim()) addRemark(enquiry.id, note.trim());
    onSaved?.();
    onClose();
  };

  return (
    <Sheet
      open={open}
      onClose={onClose}
      testId="next-action-sheet"
      title={isAppointment ? "When is the visit / demo?" : "When should we contact them next?"}
    >
      <div className="space-y-4">
        <div className="grid grid-cols-3 gap-2">
          {quick.map((q) => {
            const qDate = todayPlusISODate(q.days);
            const active = dateStr === qDate;
            return (
              <button
                key={q.label}
                data-testid={`next-quick-${q.days}`}
                onClick={() => setDateStr(qDate)}
                className={`rounded-xl border px-2 py-2.5 text-sm font-semibold transition-colors ${
                  active
                    ? "border-rose-400 bg-rose-50 text-rose-700 ring-2 ring-rose-300"
                    : "border-zinc-200 bg-white text-zinc-700 hover:bg-zinc-50"
                }`}
              >
                {q.label}
                <span className="block text-[11px] font-normal text-zinc-400">{formatDate(buildNextDate(qDate, null))}</span>
              </button>
            );
          })}
        </div>

        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="mb-1 flex items-center gap-1.5 text-xs font-medium uppercase tracking-wider text-zinc-500">
              <Calendar size={13} /> Pick a date
            </span>
            <input
              data-testid="next-date-input"
              type="date"
              value={dateStr}
              min={todayPlusISODate(0)}
              onChange={(e) => setDateStr(e.target.value)}
              className="w-full rounded-lg border border-zinc-300 px-3 py-2.5 text-sm"
            />
          </label>
          {isAppointment && (
            <label className="block">
              <span className="mb-1 flex items-center gap-1.5 text-xs font-medium uppercase tracking-wider text-zinc-500">
                <Clock size={13} /> Time
              </span>
              <input
                data-testid="next-time-input"
                type="time"
                value={timeStr}
                onChange={(e) => setTimeStr(e.target.value)}
                className="w-full rounded-lg border border-zinc-300 px-3 py-2.5 text-sm"
              />
            </label>
          )}
        </div>

        {!noteOpen ? (
          <button
            data-testid="next-add-note"
            onClick={() => setNoteOpen(true)}
            className="text-xs font-medium text-rose-600 hover:underline"
          >
            + Add a quick note
          </button>
        ) : (
          <input
            data-testid="next-note-input"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="e.g. Still busy, wants pricing first"
            className="w-full rounded-lg border border-zinc-300 px-3 py-2.5 text-sm"
          />
        )}

        <PrimaryButton data-testid="next-action-save" onClick={save} className="w-full">
          Save &amp; set reminder
        </PrimaryButton>
      </div>
    </Sheet>
  );
};

// -------------------- Enquiry Detail --------------------
export const EnquiryDetailSheet = ({ enquiry, onClose }) => {
  const { changeStage, convertEnquiry, costume, stages, undoStage, moveEnquiry, deleteEnquiry, lines, teamMembers, meId, myRole, updateEnquiry } = useJigyasa();
  const [showLost, setShowLost] = useState(false);
  const [showWA, setShowWA] = useState(false);
  const [showConverted, setShowConverted] = useState(false);
  const [showHubPick, setShowHubPick] = useState(false);
  const [showValueAsk, setShowValueAsk] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showNextDate, setShowNextDate] = useState(false);
  const [showMove, setShowMove] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [nextDateAppt, setNextDateAppt] = useState(false);
  const [convertedValue, setConvertedValue] = useState(null);
  const [convertError, setConvertError] = useState(null);
  const [converting, setConverting] = useState(false);

  if (!enquiry) return null;

  const doConvert = async (prePatch) => {
    setConverting(true);
    const res = await convertEnquiry(enquiry.id, prePatch);
    setConverting(false);
    if (res.ok) {
      setConvertedValue(res.deal_value);
      setShowConverted(true);
    } else {
      setConvertError(res.error);
    }
  };

  const moveTo = async (stageKey) => {
    setConvertError(null);
    if (stageKey === "lost") {
      setShowLost(true);
      return;
    }
    if (stageKey === "signed_up") {
      // Conversion handback — contract §2.3. With a host, the lead must
      // carry a target location by conversion time (Round-trip 6, Q1).
      // Standalone (no host): ask for an optional deal value instead.
      if (costume.buttonsOn.liveStock) {
        if (!enquiry.location_id) {
          setShowHubPick(true);
          return;
        }
        await doConvert(null);
        return;
      }
      setShowValueAsk(true);
      return;
    }
    changeStage(enquiry.id, stageKey);
    // Two-tap heartbeat: after moving to any live stage, immediately ask for
    // the next action date (with a time when it's a visit/demo).
    setNextDateAppt(stageKey === APPOINTMENT_STAGE);
    setShowNextDate(true);
  };

  const nextStages = stages.filter((s) => s.key !== enquiry.stage);

  return (
    <>
      <Sheet
        open={!!enquiry}
        onClose={() => { setConvertError(null); onClose(); }}
        testId="enquiry-detail-sheet"
        title={enquiry.name}
      >
        <div className="space-y-5">
          <div className="flex flex-wrap items-center gap-2">
            <StageChip stage={enquiry.stage} />
            {enquiry.stage === "signed_up" && (
              <span
                data-testid="converted-badge"
                className="inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-semibold text-green-800"
              >
                Converted
              </span>
            )}
            <button
              data-testid="enquiry-edit-btn"
              onClick={() => setShowEdit(true)}
              title="Edit facts"
              className="ml-auto inline-flex items-center gap-1 rounded-lg border border-zinc-300 bg-white px-2.5 py-1 text-xs font-medium text-zinc-700 hover:bg-zinc-100"
            >
              <Pencil size={13} /> Edit
            </button>
          </div>

          {/* Undo — one tap to reverse the last stage move (incl. an
              accidental Won / Lost). Nothing is ever a dead-end. */}
          {enquiry.prev_stage && (() => {
            const backLabel = stages.find((s) => s.key === enquiry.prev_stage)?.label || enquiry.prev_stage;
            return (
              <div
                data-testid="undo-stage-block"
                className="flex items-center justify-between gap-3 rounded-xl border border-sky-200 bg-sky-50 p-3"
              >
                <div className="flex items-center gap-2.5">
                  <Undo2 size={18} className="shrink-0 text-sky-600" />
                  <div className="text-sm text-sky-900">
                    Changed by mistake? Put it back to <strong>{backLabel}</strong>.
                  </div>
                </div>
                <button
                  data-testid="undo-stage-btn"
                  onClick={() => { undoStage(enquiry.id); onClose(); }}
                  className="shrink-0 rounded-lg bg-sky-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-sky-700"
                >
                  Undo
                </button>
              </div>
            );
          })()}

          {/* Intelligence banners — buttons 3 & 4 */}
          <FootprintBanner enquiry={enquiry} />
          <PastEnquiryBanner enquiry={enquiry} />

          {/* Contact actions */}
          <div className="grid grid-cols-2 gap-3">
            <a
              data-testid="action-whatsapp"
              onClick={(e) => {
                e.preventDefault();
                setShowWA(true);
              }}
              href="#"
              className="inline-flex min-h-[48px] items-center justify-center gap-2 rounded-lg bg-[#25D366] px-4 font-medium text-white hover:bg-[#1DA851]"
            >
              <MessageCircle size={18} /> WhatsApp
            </a>
            <a
              data-testid="action-call"
              href={`tel:${enquiry.phone}`}
              className="inline-flex min-h-[48px] items-center justify-center gap-2 rounded-lg border border-zinc-300 bg-white px-4 font-medium text-zinc-800 hover:bg-zinc-100"
            >
              <Phone size={18} /> Call
            </a>
          </div>

          {/* Next action date — the heartbeat that stops leads going missing */}
          {isLiveStage(enquiry.stage) && (() => {
            const isAppt = enquiry.stage === APPOINTMENT_STAGE;
            const missing = needsNextDate(enquiry);
            return (
              <div
                data-testid="next-action-block"
                className={`flex items-center justify-between rounded-xl border p-3 ${
                  missing ? "border-amber-300 bg-amber-50" : "border-zinc-200 bg-zinc-50"
                }`}
              >
                <div className="flex items-center gap-2.5">
                  <Calendar size={18} className={missing ? "text-amber-600" : "text-rose-600"} />
                  <div>
                    <div className="text-xs font-medium uppercase tracking-wider text-zinc-500">
                      {isAppt ? "Next visit / demo" : "Next action"}
                    </div>
                    <div
                      data-testid="next-action-value"
                      className={`text-sm font-semibold ${missing ? "text-amber-800" : "text-zinc-900"}`}
                    >
                      {enquiry.next_followup_date
                        ? isAppt
                          ? formatDateTime(enquiry.next_followup_date)
                          : formatDate(enquiry.next_followup_date)
                        : "No date set — when next?"}
                    </div>
                  </div>
                </div>
                <button
                  data-testid="set-next-date-btn"
                  data-tour-id="jigyasa-set-next-date"
                  onClick={() => { setNextDateAppt(isAppt); setShowNextDate(true); }}
                  className="shrink-0 rounded-lg bg-rose-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-rose-700"
                >
                  {enquiry.next_followup_date ? "Change" : "Set date"}
                </button>
              </div>
            );
          })()}

          {/* Facts */}
          <div className="space-y-2 rounded-lg bg-zinc-50 p-3 text-sm">
            <Row icon={<Phone size={16} />} label="Phone" value={enquiry.phone} />
            {enquiry.email && <Row icon={<Mail size={16} />} label="Email" value={enquiry.email} />}
            {enquiry.company && <Row icon={<User size={16} />} label="Company" value={enquiry.company} />}
            <Row icon={<MapPin size={16} />} label="Came from" value={enquiry.source} />
            {enquiry.location_name && (
              <Row icon={<MapPin size={16} />} label={costume.vocab.location.charAt(0).toUpperCase() + costume.vocab.location.slice(1)} value={enquiry.location_name} />
            )}
            <Row icon={<FileText size={16} />} label="Asked for" value={`${enquiry.enquiry_type}${enquiry.seat_count ? ` · ${enquiry.seat_count} ${enquiry.seat_count > 1 ? costume.vocab.units : costume.vocab.unit}` : ""}`} />
            {enquiry.budget != null && (
              <Row icon={<IndianRupee size={16} />} label="Budget" value={formatINR(enquiry.budget)} />
            )}
            {enquiry.deal_value != null && (
              <Row icon={<IndianRupee size={16} />} label="Deal value" value={`${formatINR(enquiry.deal_value)} / month`} />
            )}
            {teamMembers && teamMembers.length > 0 && ["owner", "admin"].includes(myRole) ? (
              <div className="flex items-center gap-3 py-1">
                <span className="text-zinc-400"><User size={16} /></span>
                <span className="shrink-0 text-sm text-zinc-500">Handled by</span>
                <select
                  data-testid="enquiry-assign-select"
                  value={enquiry.assigned_to || ""}
                  onChange={(e) => updateEnquiry(enquiry.id, { assigned_to: e.target.value })}
                  className="min-w-0 flex-1 rounded-lg border border-zinc-200 bg-white px-2 py-1.5 text-sm text-zinc-800"
                >
                  <option value="">Not assigned</option>
                  {teamMembers.map((m) => (
                    <option key={m.user_id} value={m.user_id}>{m.name}</option>
                  ))}
                </select>
              </div>
            ) : (
              <Row
                icon={<User size={16} />}
                label="Handled by"
                value={
                  !enquiry.assigned_to
                    ? "Not assigned"
                    : enquiry.assigned_to === meId
                      ? "You"
                      : ((teamMembers || []).find((m) => m.user_id === enquiry.assigned_to)?.name || "A teammate")
                }
              />
            )}
            {enquiry.lost_reason && (
              <Row icon={<X size={16} />} label="Lost reason" value={enquiry.lost_reason} />
            )}
          </div>

          {enquiry.notes && (
            <div>
              <div className="mb-1 text-xs font-medium uppercase tracking-wider text-zinc-500">Notes</div>
              <p className="rounded-lg bg-zinc-50 p-3 text-sm text-zinc-700">{enquiry.notes}</p>
            </div>
          )}

          {/* Remarks timeline — every call leaves a trace */}
          <RemarksTimeline enquiry={enquiry} />

          {/* Intelligence panels — buttons 1+2 (capacity) and 5 (pricing if at sent_price) */}
          <CapacityFitPanel enquiry={enquiry} />
          {(enquiry.stage === "sent_price" || enquiry.stage === "visited" || enquiry.stage === "spoke") && (
            <PricingContextPanel enquiry={enquiry} />
          )}

          {/* Move to stage */}
          <div data-tour-id="jigyasa-move-to-section">
            <div className="mb-2 text-xs font-medium uppercase tracking-wider text-zinc-500">Move to</div>
            {convertError && (
              <div data-testid="convert-error" className="mb-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800">
                {convertError}
              </div>
            )}
            <div className="grid grid-cols-2 gap-2">
              {nextStages.map((s) => (
                <button
                  key={s.key}
                  data-testid={`move-to-${s.key}`}
                  data-tour-id={s.key === "signed_up" ? "jigyasa-mark-won" : s.key === "lost" ? "jigyasa-mark-lost" : undefined}
                  onClick={() => moveTo(s.key)}
                  disabled={converting}
                  className="inline-flex min-h-[44px] items-center justify-center rounded-lg border border-zinc-300 bg-white px-3 text-sm font-medium text-zinc-800 hover:bg-zinc-100 disabled:opacity-50"
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>

          {/* Fix a mistake — every lead can be moved to another board or
              deleted, so a trial user is never stuck with junk data. */}
          <div className="border-t border-zinc-200 pt-4">
            <div className="mb-2 text-xs font-medium uppercase tracking-wider text-zinc-500">Fix a mistake</div>
            <div className="space-y-2">
              {lines.length > 1 && (
                <button
                  data-testid="move-lead-btn"
                  onClick={() => setShowMove(true)}
                  className="inline-flex w-full min-h-[44px] items-center justify-center gap-2 rounded-lg border border-zinc-300 bg-white px-3 text-sm font-medium text-zinc-800 hover:bg-zinc-100"
                >
                  <ArrowRightLeft size={16} /> Move to another business
                </button>
              )}
              <button
                data-testid="delete-lead-btn"
                onClick={() => setShowDelete(true)}
                className="inline-flex w-full min-h-[44px] items-center justify-center gap-2 rounded-lg border border-red-200 bg-white px-3 text-sm font-medium text-red-700 hover:bg-red-50"
              >
                <Trash2 size={16} /> Delete this enquiry
              </button>
            </div>
          </div>
        </div>
      </Sheet>

      <MoveLineSheet
        open={showMove}
        enquiry={enquiry}
        onClose={() => setShowMove(false)}
        onMoved={(targetLineId, type) => {
          moveEnquiry(enquiry.id, targetLineId, type);
          setShowMove(false);
          onClose();
        }}
      />
      <DeleteEnquirySheet
        open={showDelete}
        name={enquiry.name}
        onClose={() => setShowDelete(false)}
        onConfirm={() => {
          deleteEnquiry(enquiry.id);
          setShowDelete(false);
          onClose();
        }}
      />

      <LostReasonSheet
        open={showLost}
        onClose={() => setShowLost(false)}
        onPick={(reason) => {
          changeStage(enquiry.id, "lost", { lost_reason: reason });
          setShowLost(false);
          onClose();
        }}
      />
      <NextActionPrompt
        open={showNextDate}
        onClose={() => setShowNextDate(false)}
        enquiry={enquiry}
        isAppointment={nextDateAppt}
      />
      <WhatsAppTemplateSheet
        open={showWA}
        onClose={() => setShowWA(false)}
        enquiry={enquiry}
      />
      <HubPickerSheet
        open={showHubPick}
        onClose={() => setShowHubPick(false)}
        onPick={(loc) => {
          setShowHubPick(false);
          doConvert({ location_id: loc.locationId, location_name: loc.locationName });
        }}
      />
      <DealValueSheet
        open={showValueAsk}
        onClose={() => setShowValueAsk(false)}
        onConfirm={(value) => {
          setShowValueAsk(false);
          doConvert(value ? { deal_value: value } : null);
        }}
      />
      <ConvertedSheet
        open={showConverted}
        dealValue={convertedValue}
        onClose={() => { setShowConverted(false); onClose(); }}
      />
      <EditEnquirySheet
        open={showEdit}
        enquiry={enquiry}
        onClose={() => setShowEdit(false)}
      />
    </>
  );
};

const Row = ({ icon, label, value }) => (
  <div className="flex items-start gap-2 text-sm">
    <span className="mt-0.5 text-zinc-500">{icon}</span>
    <span className="w-28 shrink-0 text-zinc-500">{label}</span>
    <span className="text-zinc-900">{value}</span>
  </div>
);

// -------------------- Move lead to another business (line) --------------------
// Conversion-critical correction: a lead filed under the wrong business can be
// moved to the right one. It starts fresh at the new board's first step and
// re-picks its type, but keeps its full history. Also frees a mistaken board
// to be deleted once its leads are moved out.
const MoveLineSheet = ({ open, enquiry, onClose, onMoved }) => {
  const { lines, costume } = useJigyasa();
  const currentId = enquiry?.line_id || "default";
  const others = lines.filter((l) => l.id !== currentId);
  const [targetId, setTargetId] = useState("");
  const target = others.find((l) => l.id === targetId);
  const types = target?.enquiry_types?.length ? target.enquiry_types : costume.enquiryTypes;
  const [type, setType] = useState("");

  useEffect(() => {
    if (open) setTargetId(others[0]?.id || "");
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setType((target?.enquiry_types?.length ? target.enquiry_types : costume.enquiryTypes)[0] || "");
  }, [targetId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!enquiry) return null;

  return (
    <Sheet open={open} onClose={onClose} testId="move-lead-sheet" title="Move to another business">
      <div className="space-y-4">
        <p className="text-sm text-zinc-600">
          Filed under the wrong business? Move this lead to the right one. It will start fresh at
          the first step there, but all its notes stay.
        </p>
        <div>
          <span className="mb-1 block text-xs font-medium uppercase tracking-wider text-zinc-500">Move to</span>
          <div className="space-y-2">
            {others.map((l) => (
              <button
                key={l.id}
                data-testid={`move-line-option-${l.id}`}
                onClick={() => setTargetId(l.id)}
                className={`block w-full rounded-lg border px-4 py-3 text-left text-sm font-medium transition-colors ${
                  targetId === l.id
                    ? "border-rose-600 bg-rose-50 text-rose-700 ring-2 ring-rose-200"
                    : "border-zinc-300 bg-white text-zinc-800 hover:bg-zinc-50"
                }`}
              >
                {l.name}
              </button>
            ))}
          </div>
        </div>
        {target && (
          <label className="block">
            <span className="mb-1 block text-xs font-medium uppercase tracking-wider text-zinc-500">Type (in the new business)</span>
            <select
              data-testid="move-line-type"
              value={type}
              onChange={(e) => setType(e.target.value)}
              className="w-full rounded-lg border border-zinc-300 bg-white px-3 py-3 text-sm"
            >
              {types.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </label>
        )}
        <div className="flex gap-2 pt-1">
          <SecondaryButton data-testid="move-lead-cancel" onClick={onClose} className="flex-1">Cancel</SecondaryButton>
          <PrimaryButton
            data-testid="move-lead-confirm"
            onClick={() => onMoved(targetId, type)}
            disabled={!targetId}
            className="flex-1"
          >
            Move lead here
          </PrimaryButton>
        </div>
      </div>
    </Sheet>
  );
};

// -------------------- Delete confirmation --------------------
const DeleteEnquirySheet = ({ open, name, onClose, onConfirm }) => (
  <Sheet open={open} onClose={onClose} testId="delete-enquiry-sheet" title="Delete this enquiry?">
    <div className="space-y-4">
      <div className="flex gap-3 rounded-lg border border-red-200 bg-red-50 p-3">
        <AlertTriangle size={20} className="mt-0.5 shrink-0 text-red-600" />
        <p className="text-sm text-red-900">
          This permanently removes <strong>{name?.replace(" (DEMO)", "")}</strong> and all its notes.
          This cannot be undone.
        </p>
      </div>
      <div className="flex gap-2">
        <SecondaryButton data-testid="delete-enquiry-cancel" onClick={onClose} className="flex-1">Keep it</SecondaryButton>
        <button
          data-testid="delete-enquiry-confirm"
          onClick={onConfirm}
          className="flex-1 inline-flex min-h-[44px] items-center justify-center rounded-lg bg-red-600 px-4 text-sm font-semibold text-white hover:bg-red-700"
        >
          Yes, delete
        </button>
      </div>
    </div>
  </Sheet>
);

// -------------------- Location (hub) picker --------------------
// Round-trip 6, Q1: a lead can be hub-agnostic while in play, but must
// carry a target location by conversion time. Options come straight
// from hostData.liveStock() — no extra host function needed.
const HubPickerSheet = ({ open, onClose, onPick }) => {
  const { hostData, costume } = useJigyasa();
  const [locs, setLocs] = useState(null);
  const vocab = costume.vocab;

  React.useEffect(() => {
    if (!open) return;
    let alive = true;
    hostData
      .liveStock()
      .then((s) => { if (alive) setLocs(s.locations); })
      .catch(() => { if (alive) setLocs([]); });
    return () => { alive = false; };
  }, [open, hostData]);

  return (
    <Sheet
      open={open}
      onClose={onClose}
      testId="hub-picker-sheet"
      title={`Which ${vocab.location} are they signing up at?`}
    >
      {locs === null ? (
        <p className="py-4 text-center text-sm text-zinc-500">Loading…</p>
      ) : locs.length === 0 ? (
        <p className="py-4 text-center text-sm text-zinc-500">No {vocab.locations} available from the host.</p>
      ) : (
        <div className="space-y-2">
          {locs.map((l) => (
            <button
              key={l.locationId}
              data-testid={`hub-option-${l.locationId}`}
              onClick={() => onPick(l)}
              className="flex w-full items-center justify-between rounded-lg border border-zinc-300 bg-white px-4 py-3 text-left text-sm text-zinc-800 hover:bg-zinc-100"
            >
              <span className="font-medium">{l.locationName}</span>
              <span className={`text-xs ${l.free > 0 ? "text-zinc-500" : "text-red-600"}`}>
                {l.free} {vocab.units} free
              </span>
            </button>
          ))}
        </div>
      )}
    </Sheet>
  );
};

// -------------------- Deal value sheet (standalone conversion) --------------------
// No host to compute pricing, so the operator can optionally enter what the
// win is worth per month — this powers the owner's "value this month" numbers.
const DealValueSheet = ({ open, onClose, onConfirm }) => {
  const [value, setValue] = useState("");
  return (
    <Sheet open={open} onClose={onClose} testId="deal-value-sheet" title="Nice — what's it worth?">
      <div className="space-y-4">
        <p className="text-sm text-zinc-600">
          Roughly how much is this worth per month? It powers your monthly value numbers. You can skip it.
        </p>
        <label className="block">
          <span className="mb-1 block text-xs font-medium uppercase tracking-wider text-zinc-500">Value per month (₹)</span>
          <input
            data-testid="deal-value-input"
            value={value}
            onChange={(e) => setValue(e.target.value.replace(/\D/g, ""))}
            placeholder="e.g. 25000"
            inputMode="numeric"
            className="w-full rounded-lg border border-zinc-300 px-3 py-3 text-sm"
          />
        </label>
        <div className="flex gap-2">
          <SecondaryButton
            data-testid="deal-value-skip"
            onClick={() => { setValue(""); onConfirm(null); }}
            className="flex-1"
          >
            Skip
          </SecondaryButton>
          <PrimaryButton
            data-testid="deal-value-confirm"
            onClick={() => { const v = value ? parseInt(value, 10) : null; setValue(""); onConfirm(v); }}
            className="flex-1"
          >
            Mark as won
          </PrimaryButton>
        </div>
      </div>
    </Sheet>
  );
};

// -------------------- Lost reason sheet --------------------
export const LostReasonSheet = ({ open, onClose, onPick }) => {
  const { lostReasons } = useJigyasa();
  const [otherText, setOtherText] = useState("");
  return (
    <Sheet open={open} onClose={onClose} testId="lost-reason-sheet" title="Why was this lost?">
      <div className="space-y-2">
        {lostReasons.map((r) => (
          <button
            key={r}
            data-testid={`lost-reason-${r}`}
            onClick={() => onPick(r === "Other" && otherText ? otherText : r)}
            className="block w-full rounded-lg border border-zinc-300 bg-white px-4 py-3 text-left text-sm text-zinc-800 hover:bg-zinc-100"
          >
            {r}
          </button>
        ))}
        <input
          data-testid="lost-reason-other-input"
          value={otherText}
          onChange={(e) => setOtherText(e.target.value)}
          placeholder="Other (type a reason)"
          className="mt-2 w-full rounded-lg border border-zinc-300 px-3 py-3 text-sm"
        />
      </div>
    </Sheet>
  );
};

// -------------------- WhatsApp template sheet --------------------
// Real wa.me deep link: opens WhatsApp with the message pre-filled.
// Nothing is sent until the operator presses Send inside WhatsApp.
export const WhatsAppTemplateSheet = ({ open, onClose, enquiry }) => {
  const { costume, companyInfo, me } = useJigyasa();

  const fill = (body) =>
    body
      .replace(/\{\{name\}\}/g, enquiry?.name?.replace(" (DEMO)", "") || "")
      .replace(/\{\{me\}\}/g, me)
      .replace(/\{\{company\}\}/g, companyInfo.name)
      .replace(/\{\{address\}\}/g, companyInfo.address)
      .replace(/\{\{quote\}\}/g, enquiry?.budget ? formatINR(enquiry.budget) + "/month" : "[rate]");

  const openWA = (t) => {
    const phone10 = normalizePhone(enquiry.phone);
    const text = encodeURIComponent(fill(t.body));
    window.open(`https://wa.me/91${phone10}?text=${text}`, "_blank", "noopener");
    onClose();
  };

  return (
    <Sheet open={open} onClose={onClose} testId="wa-template-sheet" title="Pick a WhatsApp template">
      <div className="mb-3 rounded-lg border border-zinc-200 bg-zinc-50 px-3 py-2 text-xs text-zinc-600">
        Opens WhatsApp with the message pre-filled. Nothing is sent until you press Send there.
      </div>
      <div className="space-y-3">
        {costume.waTemplates.map((t) => (
          <button
            key={t.name}
            data-testid={`wa-template-${t.name.replace(/\s+/g, "-").toLowerCase()}`}
            onClick={() => openWA(t)}
            className="block w-full rounded-lg border border-zinc-300 bg-white p-3 text-left hover:bg-zinc-100"
          >
            <div className="mb-1 text-sm font-semibold text-zinc-900">{t.name}</div>
            <div className="text-sm text-zinc-600">{fill(t.body)}</div>
          </button>
        ))}
      </div>
    </Sheet>
  );
};

// -------------------- Add enquiry sheet --------------------
// Two lookup patterns (Round-trip 10):
//   lookupTrigger 'auto'   → name-first layout, footprint lookup fires at 10 digits
//   lookupTrigger 'button' → phone-first layout with an explicit Search button
//                            (Kreta house style — pilot uses this)
// Both share a save-time safety net: a silent re-check on Save so a
// duplicate can never slip through unseen.
export const AddEnquirySheet = ({ open, onClose }) => {
  const { addEnquiry, orgSettings, demoMode, hostData, costume, lookupTrigger, enquiryTypes, stages, lines, activeLine, setActiveLine, teamMembers, meId } = useJigyasa();
  const sourceOptions = orgSettings.sources;
  // "Who's handling this?" — only when the host provides teamMembers (team
  // orgs, owner/admin; the prop is null for solo orgs and staff). Defaults to
  // the person creating the enquiry; saves the member's user_id.
  const assignableMembers = (teamMembers || []).filter((m) => m.user_id);
  // Stages you can start a lead at. Won/Lost are left out — they're reached via
  // the "Move to" actions that capture the deal value / lost reason properly.
  const startStages = stages.filter((s) => s.key !== "signed_up" && s.key !== "lost");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [source, setSource] = useState(sourceOptions[0]);
  const [customerType, setCustomerType] = useState("individual");
  const [handledBy, setHandledBy] = useState(meId || "");
  const [showOptional, setShowOptional] = useState(false);
  const [enquiryType, setEnquiryType] = useState(costume.enquiryTypes[0]);
  const [stage, setStage] = useState("new");
  const [apptDate, setApptDate] = useState(todayPlusISODate(0));
  const [apptTime, setApptTime] = useState("10:00");
  const [apptError, setApptError] = useState("");
  const [seatCount, setSeatCount] = useState("");
  const [company, setCompany] = useState("");
  const [email, setEmail] = useState("");
  const [locationId, setLocationId] = useState("");
  const [locs, setLocs] = useState([]);
  const [notes, setNotes] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [footprint, setFootprint] = useState(null);
  const [dismissedFor, setDismissedFor] = useState(null);
  const [noMatch, setNoMatch] = useState(false);
  const [saveGuard, setSaveGuard] = useState(null);
  const lookupSeq = React.useRef(0);
  const autoFilled = React.useRef({ name: false, company: false });

  // Hub options for the optional "preferred hub" field (Round-trip 6, Q1).
  // Standalone (no host): the costume switches liveStock off — no fetch.
  React.useEffect(() => {
    if (!open || !costume.buttonsOn.liveStock) return;
    let alive = true;
    hostData
      .liveStock()
      .then((s) => { if (alive) setLocs(s.locations); })
      .catch(() => { if (alive) setLocs([]); });
    return () => { alive = false; };
  }, [open, hostData]);

  const runLookup = React.useCallback(async (p10) => {
    if (!costume.buttonsOn.customerFootprint) return null;
    const seq = ++lookupSeq.current;
    try {
      const res = await hostData.customerFootprint({ phone: p10 });
      if (lookupSeq.current !== seq) return null;
      return res && res.matched ? res : null;
    } catch {
      return null;
    }
  }, [hostData, costume]);

  const applyMatch = React.useCallback((res) => {
    setFootprint(res);
    setNoMatch(false);
    // Auto-fill name + company — never overwrite operator-typed text.
    setName((cur) => {
      if (!cur.trim() || autoFilled.current.name) {
        autoFilled.current.name = true;
        return res.customer.primaryContactName || cur;
      }
      return cur;
    });
    setCompany((cur) => {
      if (!cur.trim() || autoFilled.current.company) {
        autoFilled.current.company = true;
        return res.customer.customerName || cur;
      }
      return cur;
    });
  }, []);

  // 'auto' mode only: fire at exactly 10 normalised digits (Round-trip 8).
  React.useEffect(() => {
    if (!open || lookupTrigger !== "auto") return;
    const p10 = normalizePhone(phone);
    if (p10.length !== 10 || dismissedFor === p10) {
      setFootprint(null);
      return;
    }
    let cancelled = false;
    runLookup(p10).then((res) => {
      if (cancelled) return;
      if (res) applyMatch(res);
      else setFootprint(null);
    });
    return () => { cancelled = true; };
  }, [phone, open, dismissedFor, lookupTrigger, runLookup, applyMatch]);

  // 'button' mode: explicit search.
  const manualSearch = async () => {
    const p10 = normalizePhone(phone);
    if (p10.length !== 10) {
      setPhoneError("Enter a 10-digit number to search.");
      return;
    }
    setPhoneError("");
    const res = await runLookup(p10);
    if (res) applyMatch(res);
    else {
      setFootprint(null);
      setNoMatch(true);
    }
  };

  const dismissFootprint = () => {
    setDismissedFor(normalizePhone(phone));
    setFootprint(null);
    setNoMatch(false);
    if (autoFilled.current.name) { setName(""); autoFilled.current.name = false; }
    if (autoFilled.current.company) { setCompany(""); autoFilled.current.company = false; }
  };

  const reset = () => {
    setName(""); setPhone(""); setSource(sourceOptions[0]);
    setCustomerType("individual");
    setHandledBy(meId || "");
    setShowOptional(false); setEnquiryType(costume.enquiryTypes[0]); setStage("new");
    setSeatCount(""); setCompany(""); setEmail(""); setLocationId(""); setNotes(""); setPhoneError("");
    setFootprint(null); setDismissedFor(null); setNoMatch(false); setSaveGuard(null);
    autoFilled.current = { name: false, company: false };
  };

  const doSave = () => {
    const cleanName = name.trim();
    const loc = locs.find((l) => l.locationId === locationId) || null;
    // Starting straight at the appointment stage carries its real date+time,
    // so the engine's "contact today" auto-stamp never fakes an overdue demo.
    const apptIso = stage === APPOINTMENT_STAGE ? buildNextDate(apptDate, apptTime || "10:00") : null;
    if (stage === APPOINTMENT_STAGE && !apptIso) {
      setApptError("Pick the date for this visit / demo.");
      setShowOptional(true);
      return;
    }
    addEnquiry({
      name: demoMode && !cleanName.includes("(DEMO)") ? cleanName + " (DEMO)" : cleanName,
      phone: normalizePhone(phone), // stored as 10 digits (contract §4)
      source: source || sourceOptions[0],
      customer_type: customerType,
      enquiry_type: enquiryType,
      stage,
      ...(apptIso ? { next_followup_date: apptIso } : {}),
      seat_count: seatCount ? parseInt(seatCount, 10) : null,
      company: company.trim() || null,
      email: email.trim() || null,
      location_id: loc ? loc.locationId : null,
      location_name: loc ? loc.locationName : null,
      notes,
      ...(assignableMembers.length && handledBy ? { assigned_to: handledBy } : {}),
    });
    reset();
    onClose();
  };

  const handleSave = async () => {
    if (!phoneIsValid(phone)) {
      setPhoneError("Phone must be a 10-digit Indian number.");
      return;
    }
    if (!name.trim()) return;
    // An appointment start MUST carry its real date — never fall through to
    // the engine's "contact today" auto-stamp (fake overdue demo).
    if (stage === APPOINTMENT_STAGE && !buildNextDate(apptDate, apptTime || "10:00")) {
      setApptError("Pick the date for this visit / demo.");
      setShowOptional(true);
      return;
    }
    // Save-time safety net (both modes): if no lookup result is on
    // screen and this number wasn't dismissed, check once before saving.
    const p10 = normalizePhone(phone);
    if (!footprint && dismissedFor !== p10) {
      const res = await runLookup(p10);
      if (res) {
        setSaveGuard(res);
        return;
      }
    }
    doSave();
  };

  const onPhoneChange = (e) => {
    setPhone(e.target.value);
    setPhoneError("");
    setNoMatch(false);
    setSaveGuard(null);
  };

  const phoneField = (
    <Field label="Phone (10 digits)">
      <div className="flex gap-2">
        <input
          data-testid="add-enquiry-phone"
          data-tour-id="jigyasa-add-enquiry-phone"
          value={phone}
          onChange={onPhoneChange}
          onKeyDown={(e) => { if (e.key === "Enter" && lookupTrigger === "button") manualSearch(); }}
          placeholder="98xxxxxxxx"
          inputMode="numeric"
          className="min-w-0 flex-1 rounded-lg border border-zinc-300 px-3 py-3 text-sm"
        />
        {lookupTrigger === "button" && costume.buttonsOn.customerFootprint && (
          <button
            data-testid="add-enquiry-search-btn"
            onClick={manualSearch}
            disabled={normalizePhone(phone).length !== 10}
            className="inline-flex shrink-0 items-center gap-1.5 rounded-lg bg-rose-600 px-4 text-sm font-medium text-white hover:bg-rose-700 disabled:opacity-40"
          >
            <Search size={14} /> Search
          </button>
        )}
      </div>
      {phoneError && (
        <p data-testid="add-enquiry-phone-error" className="mt-1 text-xs text-red-600">{phoneError}</p>
      )}
    </Field>
  );

  const lookupFeedback = (
    <>
      {noMatch && (
        <p data-testid="add-enquiry-no-match" className="rounded-lg bg-zinc-50 px-3 py-2 text-xs text-zinc-600">
          No existing client with this number — fresh enquiry.
        </p>
      )}
      {footprint && footprint.matched && (
        <div data-testid="add-enquiry-footprint-banner" className="rounded-lg border border-emerald-300 bg-emerald-50 p-3">
          <div className="mb-1 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-emerald-800">
            <Users size={14} /> {costume.copy.footprintBannerLabel}
          </div>
          <p className="text-sm text-emerald-900">
            <strong>{footprint.customer.customerName}</strong> — client since {footprint.customer.since}.
            Currently: {footprint.customer.plan}, {formatINR(footprint.customer.monthlyValue)}/mo.{" "}
            {footprint.customer.openComplaints} open ticket{footprint.customer.openComplaints === 1 ? "" : "s"}.
          </p>
          {footprint.customer.notes && (
            <p className="mt-1 text-xs text-emerald-800">{footprint.customer.notes}</p>
          )}
          <button
            data-testid="add-enquiry-footprint-dismiss"
            onClick={dismissFootprint}
            className="mt-2 text-xs font-medium text-emerald-900 underline underline-offset-2"
          >
            {costume.copy.footprintInlineDismiss}
          </button>
        </div>
      )}
    </>
  );

  const nameField = (
    <Field label="Name">
      <input
        data-testid="add-enquiry-name"
        data-tour-id="jigyasa-add-enquiry-name"
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="e.g. Rakesh Kumar"
        className="w-full rounded-lg border border-zinc-300 px-3 py-3 text-sm"
      />
    </Field>
  );

  // Which pipeline this lead goes into. Only shown when the org runs more than
  // one — picking it switches the form's stages/types to that pipeline's, and
  // the lead is filed there on save.
  const onLineChange = (id) => {
    setActiveLine(id);
    const ln = lines.find((l) => l.id === id);
    setStage("new");
    setEnquiryType((ln?.enquiry_types?.length ? ln.enquiry_types : costume.enquiryTypes)[0]);
  };
  const pipelineField = lines.length > 1 && (
    <Field label="Pipeline">
      <select
        data-testid="add-enquiry-line"
        value={activeLine.id}
        onChange={(e) => onLineChange(e.target.value)}
        className="w-full rounded-lg border border-zinc-300 bg-white px-3 py-3 text-sm"
      >
        {lines.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
      </select>
      <p className="mt-1 text-xs text-zinc-500">Which line of business is this for?</p>
    </Field>
  );

  return (
    <Sheet open={open} onClose={() => { reset(); onClose(); }} testId="add-enquiry-sheet" title="New enquiry">
      <div className="space-y-3">
        {pipelineField}
        {lookupTrigger === "button" ? (
          <>
            {phoneField}
            {lookupFeedback}
            {nameField}
          </>
        ) : (
          <>
            {nameField}
            {phoneField}
            {lookupFeedback}
          </>
        )}
        <Field label="Where did they find you?">
          <select
            data-testid="add-enquiry-source"
            data-tour-id="jigyasa-add-enquiry-source"
            value={source}
            onChange={(e) => setSource(e.target.value)}
            className="w-full rounded-lg border border-zinc-300 bg-white px-3 py-3 text-sm"
          >
            {sourceOptions.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </Field>

        <Field label="Customer type">
          <div className="grid grid-cols-2 gap-2" data-tour-id="jigyasa-add-enquiry-custtype">
            {[{ v: "individual", l: "Individual" }, { v: "corporate", l: "Corporate" }].map((o) => (
              <button
                key={o.v}
                type="button"
                data-testid={`add-enquiry-customer-type-${o.v}`}
                onClick={() => setCustomerType(o.v)}
                className={`rounded-lg border px-3 py-3 text-sm font-medium transition-colors ${
                  customerType === o.v ? "border-rose-600 bg-rose-600 text-white" : "border-zinc-300 text-zinc-700 hover:bg-zinc-50"
                }`}
              >
                {o.l}
              </button>
            ))}
          </div>
        </Field>

        {customerType === "corporate" && (
          <Field label="Company name">
            <input
              data-testid="add-enquiry-company"
              value={company}
              onChange={(e) => setCompany(e.target.value)}
              placeholder="e.g. Acme Pvt Ltd"
              className="w-full rounded-lg border border-zinc-300 px-3 py-3 text-sm"
            />
            <p className="mt-1 text-xs text-zinc-400">When this enquiry is signed up, it's added to your Customers list automatically.</p>
          </Field>
        )}

        {assignableMembers.length > 0 && (
          <Field label="Who's handling this?">
            <select
              data-testid="add-enquiry-handled-by"
              value={handledBy}
              onChange={(e) => setHandledBy(e.target.value)}
              className="w-full rounded-lg border border-zinc-300 bg-white px-3 py-3 text-sm"
            >
              {assignableMembers.map((m) => (
                <option key={m.user_id} value={m.user_id}>
                  {m.user_id === meId ? `${m.name} (you)` : m.name}
                </option>
              ))}
            </select>
            <p className="mt-1 text-xs text-zinc-500">This lead will sit on their board and follow-up list.</p>
          </Field>
        )}

        <button
          data-testid="add-enquiry-toggle-optional"
          data-tour-id="jigyasa-add-enquiry-optional"
          onClick={() => setShowOptional((s) => !s)}
          className="text-sm text-zinc-600 underline-offset-4 hover:underline"
        >
          {showOptional ? "Hide optional fields" : "+ Optional details"}
        </button>

        {showOptional && (
          <div className="space-y-3 rounded-lg bg-zinc-50 p-3" data-tour-id="jigyasa-add-enquiry-optional-block">
            <Field label="Starting stage">
              <select
                data-testid="add-enquiry-stage"
                value={stage}
                onChange={(e) => setStage(e.target.value)}
                className="w-full rounded-lg border border-zinc-300 bg-white px-3 py-3 text-sm"
              >
                {startStages.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
              </select>
              <p className="mt-1 text-xs text-zinc-500">Pick a later step if you've already spoken to or met them. Defaults to the first step.</p>
            </Field>
            {stage === APPOINTMENT_STAGE && (
              <div data-testid="add-enquiry-appt-block" className="rounded-lg border border-sky-200 bg-sky-50 p-3">
                <div className="mb-2 text-xs font-medium uppercase tracking-wider text-sky-800">
                  When is the visit / demo?
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <label className="block">
                    <span className="mb-1 flex items-center gap-1.5 text-xs font-medium uppercase tracking-wider text-zinc-500">
                      <Calendar size={13} /> Date
                    </span>
                    <input
                      data-testid="add-enquiry-appt-date"
                      type="date"
                      value={apptDate}
                      min={todayPlusISODate(0)}
                      onChange={(e) => { setApptDate(e.target.value); setApptError(""); }}
                      className="w-full rounded-lg border border-zinc-300 px-3 py-2.5 text-sm"
                    />
                  </label>
                  <label className="block">
                    <span className="mb-1 flex items-center gap-1.5 text-xs font-medium uppercase tracking-wider text-zinc-500">
                      <Clock size={13} /> Time
                    </span>
                    <input
                      data-testid="add-enquiry-appt-time"
                      type="time"
                      value={apptTime}
                      onChange={(e) => { setApptTime(e.target.value); setApptError(""); }}
                      className="w-full rounded-lg border border-zinc-300 px-3 py-2.5 text-sm"
                    />
                  </label>
                </div>
                {apptError && (
                  <p data-testid="add-enquiry-appt-error" className="mt-2 text-xs text-red-600">{apptError}</p>
                )}
              </div>
            )}
            <Field label="What are they asking for?">
              <select
                data-testid="add-enquiry-type"
                value={enquiryType}
                onChange={(e) => setEnquiryType(e.target.value)}
                className="w-full rounded-lg border border-zinc-300 bg-white px-3 py-3 text-sm"
              >
                {enquiryTypes.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </Field>
            {costume.vocab.quantityFieldLabel && (
              <Field label={costume.vocab.quantityFieldLabel}>
                <input
                  data-testid="add-enquiry-seats"
                  value={seatCount}
                  onChange={(e) => setSeatCount(e.target.value.replace(/\D/g, ""))}
                  placeholder="e.g. 5"
                  inputMode="numeric"
                  className="w-full rounded-lg border border-zinc-300 px-3 py-3 text-sm"
                />
              </Field>
            )}
            <Field label="Email (optional)">
              <input
                data-testid="add-enquiry-email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="e.g. rakesh@example.com"
                inputMode="email"
                type="email"
                className="w-full rounded-lg border border-zinc-300 px-3 py-3 text-sm"
              />
            </Field>
            {locs.length > 0 && (
              <Field label={`Preferred ${costume.vocab.location} (optional)`}>
                <select
                  data-testid="add-enquiry-location"
                  value={locationId}
                  onChange={(e) => setLocationId(e.target.value)}
                  className="w-full rounded-lg border border-zinc-300 bg-white px-3 py-3 text-sm"
                >
                  <option value="">Not decided yet</option>
                  {locs.map((l) => (
                    <option key={l.locationId} value={l.locationId}>
                      {l.locationName} ({l.free} {costume.vocab.units} free)
                    </option>
                  ))}
                </select>
              </Field>
            )}
            {customerType !== "corporate" && (
              <Field label="Company (helps spot existing clients)">
                <input
                  data-testid="add-enquiry-company-optional"
                  value={company}
                  onChange={(e) => setCompany(e.target.value)}
                  placeholder="e.g. TechCo"
                  className="w-full rounded-lg border border-zinc-300 px-3 py-3 text-sm"
                />
              </Field>
            )}
            <Field label="Notes">
              <textarea
                data-testid="add-enquiry-notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                className="w-full rounded-lg border border-zinc-300 px-3 py-3 text-sm"
              />
            </Field>
          </div>
        )}

        {saveGuard && (
          <div data-testid="add-enquiry-save-guard" className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
            This number belongs to <strong>{saveGuard.customer.customerName}</strong> — already a client.
            Save as a new enquiry anyway?
            <div className="mt-2 flex gap-2">
              <button
                data-testid="save-guard-review"
                onClick={() => { applyMatch(saveGuard); setSaveGuard(null); }}
                className="rounded-lg border border-amber-400 bg-white px-3 py-2 text-xs font-medium text-amber-900 hover:bg-amber-100"
              >
                Show details
              </button>
              <button
                data-testid="save-guard-confirm"
                onClick={() => { setSaveGuard(null); doSave(); }}
                className="rounded-lg bg-rose-600 px-3 py-2 text-xs font-medium text-white hover:bg-rose-700"
              >
                Save anyway
              </button>
            </div>
          </div>
        )}

        <div className="flex gap-2 pt-2">
          <SecondaryButton onClick={() => { reset(); onClose(); }} className="flex-1" data-testid="add-enquiry-cancel">Cancel</SecondaryButton>
          <PrimaryButton onClick={handleSave} className="flex-1" data-testid="add-enquiry-save" data-tour-id="jigyasa-add-enquiry-save">Save</PrimaryButton>
        </div>
      </div>
    </Sheet>
  );
};

const Field = ({ label, children }) => (
  <label className="block">
    <span className="mb-1 block text-xs font-medium uppercase tracking-wider text-zinc-500">{label}</span>
    {children}
  </label>
);

const ConvertedSheet = ({ open, onClose, dealValue }) => (
  <Sheet open={open} onClose={onClose} testId="converted-sheet" title="Lead converted">
    <div className="space-y-4 py-2 text-center">
      <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-green-100 text-green-700">
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
          <path d="M20 6L9 17l-5-5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </div>
      <p className="text-sm text-zinc-700">
        The lead has been handed over and marked Signed up
        {dealValue != null ? (
          <> with a monthly value of <strong data-testid="converted-deal-value">{formatINR(dealValue)}</strong>.</>
        ) : (
          "."
        )}
      </p>
      <PrimaryButton onClick={onClose} data-testid="converted-ok" className="w-full">OK</PrimaryButton>
    </div>
  </Sheet>
);


// -------------------- Edit enquiry sheet --------------------
// Round-trip 11 (13 Jun 2026) request from the Kreta host: operators need
// to correct factual data on a saved enquiry — name, phone, email, company,
// type, seats, source, notes. Stage stays out (the "Move to" / convert
// flow remains the only path to stage changes, so the auto-logged stage
// remarks aren't bypassed). created_at stays out too — a back-dated edit
// would skew analytics. Every save writes a system remark with the list
// of changed fields so the timeline carries a clean audit trail.
export const EditEnquirySheet = ({ open, enquiry, onClose }) => {
  const { updateEnquiry, addRemark, orgSettings, costume, enquiryTypes } = useJigyasa();
  const sourceOptions = orgSettings.sources;

  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [company, setCompany] = useState("");
  const [source, setSource] = useState(sourceOptions[0]);
  const [customerType, setCustomerType] = useState("individual");
  const [enquiryType, setEnquiryType] = useState(costume.enquiryTypes[0]);
  const [seatCount, setSeatCount] = useState("");
  const [notes, setNotes] = useState("");
  const [phoneError, setPhoneError] = useState("");

  // Pre-fill whenever the sheet is (re)opened on a fresh enquiry.
  React.useEffect(() => {
    if (!open || !enquiry) return;
    setName(enquiry.name || "");
    setPhone(enquiry.phone || "");
    setEmail(enquiry.email || "");
    setCompany(enquiry.company || "");
    setSource(enquiry.source || sourceOptions[0]);
    setCustomerType(enquiry.customer_type || "individual");
    setEnquiryType(enquiry.enquiry_type || costume.enquiryTypes[0]);
    setSeatCount(enquiry.seat_count != null ? String(enquiry.seat_count) : "");
    setNotes(enquiry.notes || "");
    setPhoneError("");
  }, [open, enquiry, sourceOptions, costume.enquiryTypes]);

  if (!enquiry) return null;

  // Phone footprint-dismissal cache from Round-trip 8 lives inside
  // AddEnquirySheet only — editing a saved enquiry's phone has no
  // bearing on it, so no special handling required.

  const handleSave = () => {
    if (!phoneIsValid(phone)) {
      setPhoneError("Phone must be a 10-digit Indian number.");
      return;
    }
    const cleanName = name.trim();
    if (!cleanName) return;

    const next = {
      name: cleanName,
      phone: normalizePhone(phone),
      email: email.trim() || null,
      company: company.trim() || null,
      source: source || sourceOptions[0],
      customer_type: customerType,
      enquiry_type: enquiryType,
      seat_count: seatCount ? parseInt(seatCount, 10) : null,
      notes: notes,
    };

    // Compute diff so we only write fields that actually changed.
    // This keeps the audit remark accurate and avoids spurious writes.
    const FIELDS = [
      ["name", "Name"],
      ["phone", "Phone"],
      ["email", "Email"],
      ["company", "Company"],
      ["source", "Came from"],
      ["customer_type", "Customer type"],
      ["enquiry_type", "Asked for"],
      ["seat_count", costume.vocab.quantityFieldLabel ? "Seats" : "Quantity"],
      ["notes", "Notes"],
    ];
    const patch = {};
    const changedLabels = [];
    for (const [key, label] of FIELDS) {
      const before = enquiry[key] ?? null;
      const after = next[key] ?? null;
      if (before !== after) {
        patch[key] = next[key];
        changedLabels.push(label);
      }
    }

    if (changedLabels.length === 0) {
      onClose();
      return;
    }

    updateEnquiry(enquiry.id, patch);
    addRemark(
      enquiry.id,
      `Edited facts: ${changedLabels.join(", ").toLowerCase()}.`,
      { system: true }
    );
    onClose();
  };

  return (
    <Sheet open={open} onClose={onClose} testId="edit-enquiry-sheet" title="Edit enquiry">
      <div className="space-y-3">
        <Field label="Name">
          <input
            data-testid="edit-enquiry-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full rounded-lg border border-zinc-300 px-3 py-3 text-sm"
          />
        </Field>

        <Field label="Phone (10 digits)">
          <input
            data-testid="edit-enquiry-phone"
            value={phone}
            onChange={(e) => { setPhone(e.target.value); setPhoneError(""); }}
            placeholder="98xxxxxxxx"
            inputMode="numeric"
            className="w-full rounded-lg border border-zinc-300 px-3 py-3 text-sm"
          />
          {phoneError && (
            <p data-testid="edit-enquiry-phone-error" className="mt-1 text-xs text-red-600">{phoneError}</p>
          )}
        </Field>

        <Field label="Email (optional)">
          <input
            data-testid="edit-enquiry-email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            inputMode="email"
            type="email"
            className="w-full rounded-lg border border-zinc-300 px-3 py-3 text-sm"
          />
        </Field>

        <Field label="Company (optional)">
          <input
            data-testid="edit-enquiry-company"
            value={company}
            onChange={(e) => setCompany(e.target.value)}
            className="w-full rounded-lg border border-zinc-300 px-3 py-3 text-sm"
          />
        </Field>

        <Field label="Where did they find you?">
          <select
            data-testid="edit-enquiry-source"
            value={source}
            onChange={(e) => setSource(e.target.value)}
            className="w-full rounded-lg border border-zinc-300 bg-white px-3 py-3 text-sm"
          >
            {sourceOptions.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </Field>

        <Field label="Customer type">
          <div className="grid grid-cols-2 gap-2">
            {[{ v: "individual", l: "Individual" }, { v: "corporate", l: "Corporate" }].map((o) => (
              <button
                key={o.v}
                type="button"
                data-testid={`edit-enquiry-customer-type-${o.v}`}
                onClick={() => setCustomerType(o.v)}
                className={`rounded-lg border px-3 py-3 text-sm font-medium transition-colors ${
                  customerType === o.v ? "border-rose-600 bg-rose-600 text-white" : "border-zinc-300 text-zinc-700 hover:bg-zinc-50"
                }`}
              >
                {o.l}
              </button>
            ))}
          </div>
        </Field>

        <Field label="What are they asking for?">
          <select
            data-testid="edit-enquiry-type"
            value={enquiryType}
            onChange={(e) => setEnquiryType(e.target.value)}
            className="w-full rounded-lg border border-zinc-300 bg-white px-3 py-3 text-sm"
          >
            {enquiryTypes.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </Field>

        {costume.vocab.quantityFieldLabel && (
          <Field label={costume.vocab.quantityFieldLabel}>
            <input
              data-testid="edit-enquiry-seats"
              value={seatCount}
              onChange={(e) => setSeatCount(e.target.value.replace(/\D/g, ""))}
              placeholder="e.g. 5"
              inputMode="numeric"
              className="w-full rounded-lg border border-zinc-300 px-3 py-3 text-sm"
            />
          </Field>
        )}

        <Field label="Notes">
          <textarea
            data-testid="edit-enquiry-notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            className="w-full rounded-lg border border-zinc-300 px-3 py-3 text-sm"
          />
        </Field>

        <div className="flex gap-2 pt-2">
          <SecondaryButton onClick={onClose} className="flex-1" data-testid="edit-enquiry-cancel">Cancel</SecondaryButton>
          <PrimaryButton onClick={handleSave} className="flex-1" data-testid="edit-enquiry-save">Save</PrimaryButton>
        </div>
      </div>
    </Sheet>
  );
};
